import React from 'react';
import mainStyles from '../WorkerManagementComponent/WorkerManagementComponent.module.css';
import styles from './styles.module.css'
import { Dialog, DialogTitle, DialogActions, DialogContent, Grid, IconButton, Container, Button } from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import { Add, ArrowDownward, ArrowUpward, DeleteForever } from '@material-ui/icons';
import { CustomSkill } from '../../models/AgentSkills/CustomSkill';
import { AgentSkillsActions } from '../../actions/AgentSkillsActions';
import { RedInverseBtn, BlueBtn } from '../Styled/StyledButtons';
import { CustomSkillsTypes, SKILL_TYPE_ENUM } from '../../models/AgentSkills/CustomSkillsTypes';

export interface IProps {
  open: boolean
  toggle: () => void,
  addNewSkills: () => void
}

interface IState {
  newSkill: CustomSkill,
  errors: string[],
  saving: boolean,
}

/**
 * Create one custom skill.
 *
 * The type list comes from CustomSkillsTypes, so it grows with the vocabulary rather than with
 * a hard-coded pair of options. Picking `enum` opens the vocabulary editor below: an enum skill
 * is worthless without its allowed values, and those values are what every agent then picks
 * from, so they are collected here, in order, and shipped with the skill.
 */
export default class AddCustomSkillsComponent extends React.Component<IProps, IState> {

  state: IState = {
    newSkill: new CustomSkill(undefined),
    errors: [],
    saving: false,
  }

  /**
   * Same unanchored-regex defect as the agent value box had: `/[0-9a-zA-Z_]$|^$/g` tested the
   * last character only, so "my key!x" was accepted. Anchored, it is the rule that was meant.
   */
  handleKey(key: string) {
    const re = /^[0-9a-zA-Z_]*$/;
    if (re.test(key)) {
      let o = this.state.newSkill
      o.Key = key
      this.setState({ newSkill: o, errors: [] })
    }
  }

  save() {
    if (this.state.saving) {
      return
    }
    if (this.validate()) {
      this.setState({ saving: true })
      AgentSkillsActions.storeSkill(this.state.newSkill).then((data) => {
        if (data instanceof CustomSkill) {
          this.setState({ newSkill: new CustomSkill(undefined), errors: [], saving: false })
          this.props.addNewSkills()
        } else {
          const errors = (data.errors === undefined) ? [data.error] : data.errors
          this.setState({ errors: errors.filter((e) => e !== undefined && e !== null), saving: false })
        }

      })
    }
  }

  validate() {
    const errors: string[] = []

    if (this.state.newSkill.Key.length === 0) {
      errors.push('Key is required')
    } else if (!/[a-zA-Z]/.test(this.state.newSkill.Key)) {
      // Was `/[a-zA-Z_]$/g` - last character only, so "123_" passed and "a1" did not. The
      // message says what the rule has always claimed to be.
      errors.push('Must have one or more letters')
    }

    if (CustomSkillsTypes[this.state.newSkill.TypeId] === undefined) {
      errors.push('Choose a value type')
    }

    if (this.state.newSkill.TypeId === SKILL_TYPE_ENUM) {
      const values = this.state.newSkill.Values
      if (values.length === 0) {
        errors.push('An enum skill needs at least one allowed value')
      }
      if (values.some((value) => value.length === 0)) {
        errors.push('Allowed values cannot be blank')
      }
      // These three are the SERVER's rules, repeated rather than tightened. A value
      // is any text; what it may not do is carry edge whitespace (the server refuses
      // it rather than trimming, so ' gold' and 'gold' are two different answers to
      // the same question), exceed the indexed column, or repeat a sibling ignoring
      // case — the unique index is case-insensitive, so 'EN' and 'en' collide there
      // whatever this screen thinks.
      if (values.some((value) => value.length > 0 && value.trim() !== value)) {
        errors.push('Allowed values cannot start or end with a space')
      }
      if (values.some((value) => value.length > 190)) {
        errors.push('An allowed value cannot be longer than 190 characters')
      }
      if (
        values.some(
          (value, index) =>
            values.findIndex((other) => other.toLowerCase() === value.toLowerCase()) !== index
        )
      ) {
        errors.push('Allowed values must be unique, ignoring case')
      }
    }

    this.setState({ errors: errors })
    return errors.length === 0
  }

  handleTypeChange(typeId: number) {
    const o = this.state.newSkill
    o.TypeId = typeId
    // Only an enum carries a vocabulary. Dropping it on a type change keeps the POST body
    // honest about what was actually declared.
    if (typeId !== SKILL_TYPE_ENUM) {
      o.Values = []
    }
    this.setState({ newSkill: o, errors: [] })
  }

  // The array is replaced rather than written through, so no render is holding the copy that
  // is being changed.
  replaceValues(values: string[]) {
    const o = this.state.newSkill
    o.Values = values
    this.setState({ newSkill: o, errors: [] })
  }

  addValue() {
    this.replaceValues([...this.state.newSkill.Values, ''])
  }

  removeValue(index: number) {
    const values = [...this.state.newSkill.Values]
    values.splice(index, 1)
    this.replaceValues(values)
  }

  handleValueChange(index: number, value: string) {
    // Only the length is a keystroke rule. The charset is not: the server stores any
    // text, so a filter here made 'New York' untypeable for a value it would have
    // accepted. Edge whitespace and duplicates are refused by validate(), where they
    // can say WHY — silently dropping the key leaves the operator pressing a dead
    // keyboard with no explanation.
    if (value.length > 190) {
      return
    }
    const values = [...this.state.newSkill.Values]
    values[index] = value
    this.replaceValues(values)
  }

  moveValue(index: number, offset: number) {
    const target = index + offset
    const values = [...this.state.newSkill.Values]
    if (target < 0 || target >= values.length) {
      return
    }
    const moved = values[index]
    values[index] = values[target]
    values[target] = moved
    this.replaceValues(values)
  }

  renderValuesEditor() {
    if (this.state.newSkill.TypeId !== SKILL_TYPE_ENUM) {
      return null
    }
    const values = this.state.newSkill.Values
    return (
      <div>
        <label className={styles.FormLabel}>Allowed values</label>
        {(values.length === 0) ?
          <div className={styles.CustomSkillError}>Add at least one allowed value</div>
          :
          null
        }
        {values.map((value, index) => (
          // Keyed by position: the row is a fully controlled input with no state of its own,
          // so a reorder re-renders both rows correctly.
          <div className={styles.EnumValueRow} key={index}>
            <input
              placeholder="value"
              aria-label={'allowed value ' + (index + 1)}
              className={`${(value.length === 0) ? styles.FormInvalid : ""} ${styles.FormControl}`}
              value={value}
              onChange={(e) => { this.handleValueChange(index, e.target.value) }}
            />
            <IconButton size="small" title="Move up" aria-label="move up" disabled={index === 0} onClick={() => this.moveValue(index, -1)}><ArrowUpward fontSize="small" /></IconButton>
            <IconButton size="small" title="Move down" aria-label="move down" disabled={index === values.length - 1} onClick={() => this.moveValue(index, 1)}><ArrowDownward fontSize="small" /></IconButton>
            <IconButton size="small" title="Remove" aria-label="remove" color="secondary" onClick={() => this.removeValue(index)}><DeleteForever fontSize="small" /></IconButton>
          </div>
        ))}
        <Button color="primary" onClick={() => this.addValue()}><Add /> add value</Button>
      </div>
    )
  }

  renderErrors() {
    if (this.state.errors.length === 0) {
      return null
    }
    return (
      <ul className={`${styles.ErrorsUl} ${styles.CustomSkillError}`}>
        {this.state.errors.map((error, index) => (
          <li key={index}>{error}</li>
        ))}
      </ul>
    )
  }

  render() {
    return (<div >
      <Dialog open={this.props.open} onClose={this.props.toggle}>
        <DialogTitle className={styles.PopUpHeader}>
          Add Custom Skill
          <IconButton className={mainStyles.floatRight} onClick={this.props.toggle}>
            <ClearIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Container maxWidth={false}>
            <Grid container>
              <Grid item xs={12}>
                <div className={styles.SkillsMargin}>
                  <label className={styles.FormLabel}>Key</label>
                  <input placeholder="Key" className={`${(this.state.newSkill.Key === '') ? styles.FormInvalid : ""} ${styles.FormControl} ${styles.FullWidth}`} value={this.state.newSkill.Key} onChange={(e) => { this.handleKey(e.target.value) }} />
                  <label className={styles.FormLabel}>Type</label>
                  <select className={`${(CustomSkillsTypes[this.state.newSkill.TypeId] === undefined) ? styles.FormInvalid : ""} ${styles.FormControl}  ${styles.FullWidth}`} value={this.state.newSkill.TypeId} onChange={event => this.handleTypeChange(parseInt(event.target.value))}>
                    <option value="-1" disabled >Choose value type</option>
                    {Object.keys(CustomSkillsTypes).map((key) => (
                      <option key={key} value={key} >{CustomSkillsTypes[parseInt(key)]}</option>
                    ))}
                  </select>
                  {this.renderValuesEditor()}
                  {this.renderErrors()}
                </div>
              </Grid>
            </Grid>
          </Container>
        </DialogContent>
        <DialogActions>
          <Container maxWidth={false}>
            <div style={{ marginBottom: '24px' }}>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <RedInverseBtn fullWidth={true} onClick={this.props.toggle}>Cancel</RedInverseBtn>
                </Grid>
                <Grid item xs={6}>
                  <BlueBtn disabled={this.state.saving} fullWidth={true} onClick={() => this.save()} >Save</BlueBtn>
                </Grid>
              </Grid>
            </div>
          </Container>
        </DialogActions>
      </Dialog>

    </div>
    )
  }
}
